import type { PhraseVerdict } from '@tunasong/music-lib'
import type { Melody } from '@tunasong/schemas'
import { Button } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useEffect, useRef, useState } from 'react'
import { MelodyRoll } from '../melody-roll/melody-roll.js'
import { useVocalCopy } from '../pitch-training/use-vocal-copy.js'
import { TheoryGlyphs } from '../theory-copy/theory-glyphs.js'
import type { FeedbackView } from './feedback-view.js'
import type { Singable } from './practice-target.js'
import styles from './phrase-feedback.module.css'

/**
 * Phrase feedback (Pitch Trainer Phase 2, plan M6a steps 6.4 and 6.6; Joni §7, §8): three blocks in a fixed
 * order, the one that matters most first and nothing repeated. Block 1 takes focus. Blocks 2 and 3 are left
 * out when empty, so "on pitch" is the headline and the two actions alone.
 */

export interface PhraseFeedbackProps {
  view: FeedbackView
  /** The phrase as it was played, for the roll */
  audition: Melody
  onAgain: () => void
  onNext: () => void
  /** "Next phrase" or "Next part" in a section run (Joni §6); "Next" otherwise */
  nextLabel?: string
  /** The hardest spot's two notes */
  onHear: (melody: Melody) => void
  /** Joni §8: sing the first half, then the second */
  onSingHalf: (halves: Singable[]) => void
  /**
   * Chord-only material: "Guide tones for Chorus" and its sub-label, above block 1 in every state, "on pitch"
   * included (Joni's ruling, M6). Without it, "Every note landed." reads as the song's melody sung (§9)
   */
  context?: { title: string; subLabel: string }
}

/** Joni §7: after this many, the rest behind "Show n more" */
const shownNotes = 3

const Verdict: FC<{ verdict: PhraseVerdict; className: string }> = ({ verdict, className }) => {
  const copy = useVocalCopy(verdict.id, verdict.tokens)
  return copy ? (
    <p className={className}>
      <TheoryGlyphs>{copy.body}</TheoryGlyphs>
    </p>
  ) : null
}

export const PhraseFeedback: FC<PhraseFeedbackProps> = props => {
  const { view, audition, onAgain, onNext, nextLabel = 'Next', onHear, onSingHalf, context } = props
  const headlineRef = useRef<HTMLDivElement>(null)
  const [isAllShown, setAllShown] = useState(false)
  const notes = isAllShown ? view.offNotes : view.offNotes.slice(0, shownNotes)
  const hidden = view.offNotes.length - notes.length

  useEffect(() => {
    headlineRef.current?.focus()
  }, [])

  return (
    <section className={styles.feedback} aria-label="Feedback">
      {context ? (
        <div className={styles.context}>
          <h2 className={styles.contextTitle}>{context.title}</h2>
          <p className={styles.contextSubLabel}>{context.subLabel}</p>
        </div>
      ) : null}
      <div ref={headlineRef} tabIndex={-1} className={styles.headline} data-block="headline">
        {view.headline ? (
          <Verdict verdict={view.headline} className={styles.headlineText} />
        ) : (
          <p className={styles.score}>{`${view.scorePercent}%`}</p>
        )}
      </div>

      {view.split ? (
        <div className={styles.split}>
          <p className={styles.body}>{view.split.text}</p>
          <div className={styles.actions}>
            <Button variant="contained" onClick={() => view.split && onSingHalf(view.split.halves)}>
              Sing the first half
            </Button>
            <Button variant="outlined" onClick={onAgain}>
              Sing it whole again
            </Button>
          </div>
        </div>
      ) : null}

      {view.offNotes.length > 0 ? (
        <div className={styles.block}>
          <h3 className={styles.blockTitle} id="phrase-feedback-off-notes">
            Notes that weren’t on
          </h3>
          <span className={styles.roll} aria-hidden="true">
            <MelodyRoll melody={audition} compact={true} interactive={false} marks={view.marks} />
          </span>
          <ol className={styles.notes} aria-label="Notes that weren’t on">
            {notes.map(note => (
              <li key={note.referenceIndex} className={styles.note}>
                <span className={styles.mark} aria-hidden="true">
                  {note.mark}
                </span>
                {/* One flex item: the glyph's own span must not be spaced apart from its note */}
                <span>
                  <TheoryGlyphs>{note.text}</TheoryGlyphs>
                </span>
              </li>
            ))}
          </ol>
          {hidden > 0 ? (
            <Button variant="text" size="small" onClick={() => setAllShown(true)}>
              {`Show ${hidden} more`}
            </Button>
          ) : null}
        </div>
      ) : null}

      {view.hardest ? (
        <div className={styles.block}>
          <h3 className={styles.blockTitle}>Hardest spot</h3>
          <Verdict verdict={view.hardest.verdict} className={styles.body} />
          {/* Joni, M6a: named for its span; the visible label is the name (WCAG 2.5.3), so the bar is said once */}
          <Button variant="outlined" size="small" onClick={() => view.hardest && onHear(view.hardest.audition)}>
            {`Hear ${view.hardest.where}`}
          </Button>
        </div>
      ) : null}

      <div className={styles.actions}>
        <Button variant="outlined" onClick={onAgain}>
          Sing it again
        </Button>
        <Button variant="contained" onClick={onNext}>
          {nextLabel}
        </Button>
      </div>
    </section>
  )
}
